// src/data/claimRules.js
//
// RULE HANDLING IN THE WORKSHOP - ADD, NEVER REMOVE. Ruling 13 (2026-08-23).
//
// Bob: "Agreement rule interpretation belongs solely to the General Chairman... the LC can just
// leave the rule in and if a different rule is required add it, make note of it, and be sure to
// follow up with both the claimant and the GC."
//
// ---------------------------------------------------------------------------------------------
// THE ONE SENTENCE THIS MODULE EXISTS TO ENFORCE, from
// data\YCP_LC_GC_WORKSPACE_REQUIREMENTS_2026-08-23.md SS3:
//
//   "No `isRemoved` field, and no soft-delete that renders as absence. A rule considered and set
//    aside must remain visible as CONSIDERED AND SET ASIDE, because at a board the question 'was
//    this theory ever looked at?' has to be answerable from the record."
//
// THERE IS NO removeRule() IN THIS FILE AND THERE IS NO `isRemoved` ANYWHERE IN IT. What a Local
// Chairperson can do to a rule he disagrees with is SET IT ASIDE - which is an annotation that
// leaves the rule in the list, visibly, with his reason attached. The board question stays
// answerable because nothing ever leaves.
//
// WHETHER A GENERAL CHAIRPERSON MAY REMOVE IS NOT SETTLED AND IS NOT ASSUMED HERE. SS3: "Bob said
// interpretation is his alone; he did not say deletion is. Left open rather than inferred." So this
// module gives the GC the same two verbs as the LC and no third one.
//
// AND INTERPRETATION IS NOT A VERB HERE AT ALL. The table at SS3 gives "Interpret the agreement" to
// the GC solely and to the LC never. This module does not model interpretation, because a field an
// LC can type into is a field an LC can interpret in.
// ---------------------------------------------------------------------------------------------

import { notifyBoth, KINDS } from './notifications';

// A note is REQUIRED on both verbs. Ruling 13: "add it, MAKE NOTE OF IT". Same shape and the same
// reasoning as the outcome reason at 61.9a and the return reason at 72.3 - short, never pre-filled,
// so a blank is visibly a blank rather than a default nobody read.
export const NOTE_MIN = 4;
export const NOTE_MAX = 400;

export const ACTIONS = {
  ADD: 'add',
  SET_ASIDE: 'set_aside',
};

// The two verbs, and the absence of a third is the ruling.
export const VERBS = [
  { key: ACTIONS.ADD,       label: 'Add a rule to this claim' },
  { key: ACTIONS.SET_ASIDE, label: 'Set a rule aside (it STAYS on the claim, marked)' },
];

export function mayHandleRules(claim, user) {
  if (!claim || !user) return false;
  if (String(claim.status || '') === 'draft') return false;   // ruling 56 - a draft is its author's
  if (user.isAdmin) return true;
  if (user.role === 'general_chair') return !!user.committee && user.committee === claim.committee;
  if (user.role === 'local_chair') {
    // Record 592 (functions audit F24): and only on a rung he still holds - firestore.rules lcHolds(). At gc_review or
    // past it (or suspended) the write is refused, so the control was offered and then failed.
    return Array.isArray(user.scopes) && user.scopes.indexOf(claim.scopeKey) !== -1
        && ['submitted', 'intake', 'investigation', 'documentation_review'].indexOf(String(claim.status || '')) !== -1;
  }
  return false;
}

// A rule entry as this module understands it. `setAside` is a RECORD OF AN ACT, not a visibility
// flag - nothing anywhere may read it as "hide this row".
export function isSetAside(entry) {
  return !!entry && !!entry.setAsideAt;
}

// The list a surface renders. EVERY rule is returned, always - including every set-aside one.
// A caller that wants only the live ones has to filter deliberately and say so in its own code;
// this function will not do it for them, because a helper called `activeRules()` is exactly how a
// soft-delete gets built by accident six months later.
// A rule's identity. Rules added by an officer carry ruleId/ruleLabel; rules matched at filing carry `rule` (the
// label) and no ruleId. Section 512 (2026-09-29): the list printed "RELIED ON" with NO NAME for every filed rule and
// the set-aside form read "Set aside: undefined", because only ruleId/ruleLabel were read. Both shapes are read now.
export function ruleKey(r) { return String((r && (r.ruleId || r.rule)) || ''); }

export function rulesForDisplay(claim) {
  const list = Array.isArray(claim && claim.matchedRules) ? claim.matchedRules : [];
  return list.map(r => ({
    ...r,
    ruleId: ruleKey(r),
    ruleLabel: (r && (r.ruleLabel || r.rule)) || '',
    setAside: isSetAside(r),
    // The word a surface should print. "Set aside" is not "removed" and is not "rejected".
    stateLabel: isSetAside(r) ? 'CONSIDERED AND SET ASIDE' : 'RELIED ON',
  }));
}

function validateNote(note, errors, verbWord) {
  const n = String(note == null ? '' : note).trim();
  if (!n) {
    errors.push('A note is required. Ruling 13: ' + verbWord + ' it AND make note of it - the note is '
              + 'what a board reads when it asks why.');
  } else if (n.length < NOTE_MIN) {
    errors.push('That is too short to be a note (' + n.length + ' characters).');
  } else if (n.length > NOTE_MAX) {
    errors.push('Keep it short - ' + NOTE_MAX + ' characters at most. Detail belongs in a claim note.');
  }
  return n;
}

// ---------------------------------------------------------------------------------------------
// THE TWO VERBS. Each returns { ok, errors[], rulesPatch, notifications } - and the caller MUST
// write them in one batch.
//
// SS3.1 IS NOT A PREFERENCE: "An LC annotation writes, atomically: the annotation, a notification to
// the claimant, a notification to the GC. IF THE NOTIFICATION FAILS, THE ANNOTATION FAILS. Bob's
// word was MUST - a follow-up that can silently not happen is not a follow-up."
//
// So neither function returns a patch without notifications, and neither returns notifications
// without a patch. If the pair cannot be addressed - a claim with no author uid, or no committee -
// BOTH are refused. Recording the annotation and quietly not telling anybody is the failure the
// ruling names.
// ---------------------------------------------------------------------------------------------

export function addRule(claim, user, input) {
  const i = input || {};
  const errors = [];

  if (!mayHandleRules(claim, user)) errors.push('This claim is not yours to work.');

  const ruleId = String(i.ruleId || '').trim();
  const ruleLabel = String(i.ruleLabel || '').trim();
  if (!ruleId) errors.push('Name the rule being added.');

  const existing = Array.isArray(claim && claim.matchedRules) ? claim.matchedRules : [];
  if (ruleId && existing.some(r => r && ruleKey(r) === ruleId)) {
    errors.push('That rule is already on this claim. If it was set aside it is still there and still '
              + 'visible - set-aside is not removal, and a rule is never added twice.');
  }

  const note = validateNote(i.note, errors, 'add');

  const notes = notifyBoth({
    kind: KINDS.RULE_ADDED,
    claimId: (claim && (claim.id || claim.docId)) || '',
    committee: claim && claim.committee,
    claimantUid: claim && (claim.claimantUid || claim.createdBy),   // Record 592 (F14): the claimant, not the officer who filed for him
    title: 'A rule was added to your claim',
    body: (ruleLabel || ruleId) + ' was added. ' + note,
    actorName: (user && (user.name || user.email)) || 'Unknown',
    actorRole: (user && user.role) || 'unknown',
    at: i.at,
  });
  if (!notes.ok) errors.push.apply(errors, notes.errors);

  if (errors.length) return { ok: false, errors, rulesPatch: null, notifications: [] };

  return {
    ok: true,
    errors: [],
    rulesPatch: {
      ruleId,
      ruleLabel: ruleLabel || ruleId,
      addedBy: (user && (user.name || user.email)) || 'Unknown',
      addedByRole: (user && user.role) || 'unknown',
      addedAt: i.at || new Date().toISOString(),
      addedNote: note,
      source: 'officer',            // distinguishes it from a rule the platform matched
    },
    notifications: notes.records,
  };
}

// SET ASIDE - the nearest thing to removal that ruling 13 permits, and it is not removal.
// The entry stays in `matchedRules`. It gains a stamp and a reason. Nothing is deleted and nothing
// is hidden.
export function setAsideRule(claim, user, input) {
  const i = input || {};
  const errors = [];

  if (!mayHandleRules(claim, user)) errors.push('This claim is not yours to work.');

  const ruleId = String(i.ruleId || '').trim();
  const existing = Array.isArray(claim && claim.matchedRules) ? claim.matchedRules : [];
  const target = existing.find(r => r && ruleKey(r) === ruleId);

  if (!ruleId) errors.push('Name the rule being set aside.');
  else if (!target) errors.push('That rule is not on this claim.');
  else if (isSetAside(target)) errors.push('That rule is already set aside.');

  const note = validateNote(i.note, errors, 'set aside');

  const notes = notifyBoth({
    kind: KINDS.RULE_ANNOTATED,
    claimId: (claim && (claim.id || claim.docId)) || '',
    committee: claim && claim.committee,
    claimantUid: claim && (claim.claimantUid || claim.createdBy),   // Record 592 (F14)
    title: 'A rule on your claim was set aside',
    body: ((target && (target.ruleLabel || target.rule)) || ruleId) + ' was set aside - it REMAINS on the claim and '
        + 'remains visible. ' + note,
    actorName: (user && (user.name || user.email)) || 'Unknown',
    actorRole: (user && user.role) || 'unknown',
    at: i.at,
  });
  if (!notes.ok) errors.push.apply(errors, notes.errors);

  if (errors.length) return { ok: false, errors, rulesPatch: null, notifications: [] };

  // The WHOLE array is returned, with one entry stamped. Firestore arrayRemove/arrayUnion cannot
  // edit an element in place, and a read-modify-write of the whole list is the honest way to say
  // "this list changed" - it also makes it impossible to drop an entry by accident.
  const next = existing.map(r => (ruleKey(r) === ruleId
    ? {
        ...r,
        setAsideAt: i.at || new Date().toISOString(),
        setAsideBy: (user && (user.name || user.email)) || 'Unknown',
        setAsideByRole: (user && user.role) || 'unknown',
        setAsideNote: note,
      }
    : r));

  return { ok: true, errors: [], rulesPatch: { matchedRules: next }, notifications: notes.records };
}
